import { useEffect, useRef, useState } from "react";

const withdrawMethods = [
  {
    name: "USDT BEP20",
    icon: "ri-currency-line",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    desc: "Tether USD",
  },
  {
    name: "USDC",
    icon: "ri-coin-line",
    color: "text-sky-600",
    bg: "bg-sky-50",
    desc: "USD Coin",
  },
];

const exchanges = [
  { name: "Binance", icon: "ri-exchange-line", color: "text-yellow-600", bg: "bg-yellow-50" },
  { name: "OKX", icon: "ri-swap-line", color: "text-gray-700", bg: "bg-gray-100" },
  { name: "Bybit", icon: "ri-funds-line", color: "text-orange-600", bg: "bg-orange-50" },
  { name: "Gate.io", icon: "ri-bar-chart-line", color: "text-red-600", bg: "bg-red-50" },
];

const trustPoints = [
  {
    icon: "ri-shield-check-line",
    title: "ปลอดภัย 100%",
    desc: "ระบบเข้ารหัสข้อมูลระดับธนาคาร ข้อมูลส่วนตัวของคุณปลอดภัยแน่นอน",
    color: "from-green-500 to-emerald-400",
  },
  {
    icon: "ri-building-line",
    title: "บริษัทจดทะเบียนถูกต้อง",
    desc: "ดำเนินธุรกิจถูกกฎหมาย มีใบอนุญาตประกอบธุรกิจอย่างเป็นทางการ",
    color: "from-red-500 to-orange-400",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "ซัพพอร์ต 24/7",
    desc: "ทีมงานพร้อมช่วยเหลือตลอด 24 ชั่วโมง ผ่าน LINE และ Facebook",
    color: "from-orange-500 to-yellow-400",
  },
  {
    icon: "ri-time-line",
    title: "ถอนเงิน 24–72 ชม.",
    desc: "ถอนเงินเข้าบัญชี USDT/USDC ภายใน 24–72 ชั่วโมง ไม่มีค่าธรรมเนียมซ่อนเร้น",
    color: "from-yellow-500 to-orange-400",
  },
];

export default function TrustBadges() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="trust" className="py-20 md:py-28 bg-gray-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-green-50 text-green-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            ความน่าเชื่อถือ
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            ปลอดภัย เชื่อถือได้ จ่ายจริง
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            เราโปร่งใส ไม่มีค่าใช้จ่ายซ่อนเร้น ไม่ต้องลงทุนแม้แต่บาทเดียว
          </p>
        </div>

        {/* Withdrawal Methods + Exchange */}
        <div className="bg-white rounded-2xl p-8 md:p-10 mb-8 border border-gray-100">
          {/* Crypto Withdrawal */}
          <div className="flex flex-col md:flex-row items-start gap-8 mb-8 pb-8 border-b border-gray-100">
            <div className="md:w-1/3">
              <h3 className="text-xl font-bold text-gray-900 mb-2">ช่องทางถอนเงิน</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                รองรับการถอนเงินผ่าน Crypto ที่ปลอดภัยและรวดเร็ว
              </p>
              <div className="bg-emerald-50 rounded-xl px-4 py-3 inline-block">
                <p className="text-emerald-700 text-xs font-semibold">
                  <i className="ri-information-line mr-1"></i>
                  เหรียญ = บาท = ดอลลาร์
                </p>
                <p className="text-emerald-600 text-xs mt-1">3,000 เหรียญ ≈ ฿100 ≈ $3 USD</p>
              </div>
            </div>
            <div className="md:w-2/3 flex flex-wrap gap-4 items-center">
              {withdrawMethods.map((pm, i) => (
                <div
                  key={pm.name}
                  className={`flex items-center gap-3 ${pm.bg} px-6 py-4 rounded-xl transition-all duration-300 ${
                    visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className={`w-10 h-10 flex items-center justify-center`}>
                    <i className={`${pm.icon} text-2xl ${pm.color}`}></i>
                  </div>
                  <div>
                    <span className="font-bold text-gray-800 text-base whitespace-nowrap block">{pm.name}</span>
                    <span className="text-gray-400 text-xs">{pm.desc}</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 rounded-xl">
                <i className="ri-time-line text-orange-500 text-lg"></i>
                <div>
                  <span className="text-gray-700 text-sm font-semibold block">เวลาถอน</span>
                  <span className="text-gray-400 text-xs">24–72 ชั่วโมง</span>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 rounded-xl">
                <i className="ri-arrow-down-circle-line text-green-500 text-lg"></i>
                <div>
                  <span className="text-gray-700 text-sm font-semibold block">ขั้นต่ำ</span>
                  <span className="text-gray-400 text-xs">3,000 เหรียญ (฿100)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Exchange Partners */}
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="md:w-1/3">
              <h3 className="text-xl font-bold text-gray-900 mb-2">รองรับ Exchange ชั้นนำ</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                เชื่อมต่อกับ Exchange ชั้นนำระดับโลก รองรับการถอนเงินได้หลายช่องทาง
              </p>
            </div>
            <div className="md:w-2/3 flex flex-wrap gap-4 justify-center md:justify-start">
              {exchanges.map((ex, i) => (
                <div
                  key={ex.name}
                  className={`flex items-center gap-3 ${ex.bg} px-5 py-3 rounded-xl transition-all duration-300 ${
                    visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                  }`}
                  style={{ transitionDelay: `${200 + i * 80}ms` }}
                >
                  <div className={`w-8 h-8 flex items-center justify-center`}>
                    <i className={`${ex.icon} text-xl ${ex.color}`}></i>
                  </div>
                  <span className="font-semibold text-gray-800 text-sm whitespace-nowrap">{ex.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust Points Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {trustPoints.map((point, i) => (
            <div
              key={point.title}
              className={`bg-white rounded-2xl p-6 border border-gray-100 hover:-translate-y-1 transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${300 + i * 100}ms` }}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${point.color} mb-4`}>
                <i className={`${point.icon} text-white text-xl`}></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{point.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{point.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom disclaimer */}
        <div className="mt-10 text-center">
          <p className="text-gray-400 text-sm flex flex-wrap items-center justify-center gap-4">
            <span className="flex items-center gap-1">
              <i className="ri-lock-line text-green-500"></i>
              SSL Encrypted
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-verified-badge-line text-emerald-500"></i>
              Verified Business
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-shield-star-line text-yellow-500"></i>
              Data Protected
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-customer-service-line text-red-500"></i>
              24/7 Support
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}